<template>
  <span v-if="native && !disabled" class="wechat-action" :class="variant" :style="buttonStyle">
    <button v-if="!ready" class="native-fallback" type="button" :disabled="busy || !fallback" @click="plain">{{ label }}</button>
    <wx-open-subscribe ref="tag" :tabindex="ready ? 0 : -1" :template="template" class="native-tag" :class="{ pending: !ready }" :aria-hidden="!ready" @ready="tagReady" v-html="tagMarkup" />
  </span>
  <button v-else type="button" class="wechat-action" :class="variant" :disabled="disabled || busy || !fallback" @click="plain">{{ label }}</button>
</template>
<script>
import api from '@/api/api';
import { settings, insideWeChat } from './wechat';
import { prepareSubscription } from './share';
import { accepted, flush, newGrant } from './notifications';

function escape(s) { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }
export default {
  props: { action: Function, label: String, disabled: Boolean, collect: Boolean, variant: String, fallback: { type: Boolean, default: true } },
  data: () => ({ native: false, ready: false, buttonStyle: {}, tagMarkup: '', grant: null, busy: false, armed: false, failed: false, generation: 0, preparing: null, userTemplate: null }),
  computed: {
    // The player's own template: a spare the admin gave their WeChat, or the default.
    template() { return this.userTemplate || settings.notifyTemplateId; },
    user() { return this.$store.state.user; },
  },
  watch: {
    label() { this.refresh(); },
    disabled() { this.refresh(); },
    collect() { this.refresh(); },
    'user.id'() { this.refresh(); },
    'user.notify'() { this.refresh(); },
    'user.notifyAsked'() { this.refresh(); },
  },
  mounted() {
    this.tagError = e => this.unavailable('open-tags-error', e.detail);
    document.addEventListener('WeixinOpenTagsError', this.tagError);
    this.timer = setInterval(() => { flush(); this.prepare(); }, 30000);
    this.prepare();
  },
  beforeDestroy() {
    this.destroyed = true;
    this.invalidate();
    clearInterval(this.timer);
    document.removeEventListener('WeixinOpenTagsError', this.tagError);
  },
  methods: {
    canCollect() {
      // Nothing is collected before the player answered the reminder notice (WeChatNotifyNotice),
      // whose own button is the 'switch' variant.
      return !this.disabled && this.collect && this.user && ((this.user.notifyAsked && this.user.notify !== false) || this.variant === 'switch') &&
        this.template && insideWeChat() && !this.failed;
    },
    detachTag() {
      if (!this.boundTag) return;
      this.boundTag.removeEventListener('success', this.successHandler);
      this.boundTag.removeEventListener('error', this.errorHandler);
      this.boundTag = null;
    },
    invalidate() {
      this.generation++;
      clearTimeout(this.readyTimer);
      this.detachTag();
      this.armed = false;
      this.grant = null;
      this.native = false;
      this.ready = false;
    },
    async prepare() {
      const generation = this.generation;
      if (this.preparing === generation || this.busy || this.destroyed || !this.canCollect()) return;
      if (this.native && this.grant) return;
      this.preparing = generation;
      const user = this.user.id;
      const current = () => !this.destroyed && generation === this.generation && this.user && this.user.id === user && this.canCollect();
      try {
        const [state, linked] = await Promise.all([api.call('GET', `users/${user}/notifiable`), api.call('GET', `wechat/identities?user=${user}`)]);
        if (!current() || !state.reachable || state.full) return;
        this.userTemplate = (linked[0] && linked[0].notifyTemplateId) || null;
        // A switch-on button is allowed to collect while the preference is off. The recording key is
        // asked for only once WeChat reports the grant (notifications.js), so a button prepared long
        // before the tap still records it.
        const grant = newGrant();
        this.tagMarkup = this.buttonTemplate(this.$el);
        this.grant = grant;
        this.native = true;
        this.armed = true;
        await this.$nextTick();
        if (!current()) return;
        this.renderTag({ generation, grant, user, action: this.action });
        if (!this.ready) this.readyTimer = setTimeout(() => {
          if (current()) this.unavailable('ready-timeout');
        }, 5000);
        // Mount the populated tag and handlers before configuring the shared SDK.
        await prepareSubscription();
      } catch (e) { if (current()) this.unavailable('prepare-error', e); }
      finally { if (this.preparing === generation) this.preparing = null; }
    },
    report(event, detail) {
      const message = detail && (detail.errMsg || detail.message || detail.err_msg) || '';
      Promise.resolve().then(() => api.log(`wechat-subscribe ${event}: ${String(message).slice(0, 200)}`)).catch(() => {});
    },
    tagReady(event) {
      if (event.target !== this.$refs.tag || !this.native || !this.canCollect() || this.destroyed) return;
      clearTimeout(this.readyTimer);
      this.ready = true;
      this.report('ready');
    },
    unavailable(reason, detail) {
      if (this.destroyed) return;
      this.report(reason, detail);
      this.failed = true;
      this.invalidate();
    },
    refresh() {
      this.invalidate();
      this.$nextTick(this.prepare);
    },
    buttonTemplate(button) {
      // Capture the enabled appearance, even when the permission-only fallback is disabled.
      const disabled = button.disabled;
      button.disabled = false;
      try {
        const style = window.getComputedStyle(button);
        // Computed font sizes already include browser text scaling. Preserve that size on the
        // replacement host and native document without applying the scale a second time.
        this.buttonStyle = {
          fontFamily: style.fontFamily, fontSize: style.fontSize, fontWeight: style.fontWeight,
          fontStyle: style.fontStyle, fontVariant: style.fontVariant, lineHeight: style.lineHeight,
          letterSpacing: style.letterSpacing, wordSpacing: style.wordSpacing,
          textAlign: style.textAlign, textTransform: style.textTransform, whiteSpace: style.whiteSpace,
          webkitTextSizeAdjust: '100%', textSizeAdjust: '100%',
        };
        // The SDK needs definite dimensions when it first measures its separate document.
        const width = button.clientWidth;
        const height = button.clientHeight;
        const css = `-webkit-appearance:none;appearance:none;display:${style.display};align-items:${style.alignItems};justify-content:${style.justifyContent};box-sizing:border-box;width:${width}px;height:${height}px;margin:0;padding:${style.padding};border:0;border-radius:${style.borderRadius};background:${style.backgroundColor};color:${style.color};font-family:${style.fontFamily};font-size:${style.fontSize};font-weight:${style.fontWeight};font-style:${style.fontStyle};font-variant:${style.fontVariant};line-height:${style.lineHeight};letter-spacing:${style.letterSpacing};word-spacing:${style.wordSpacing};text-align:${style.textAlign};text-transform:${style.textTransform};white-space:${style.whiteSpace};cursor:pointer;-webkit-text-size-adjust:100%;text-size-adjust:100%;` +
          (this.variant === 'link' ? 'text-decoration:underline;' : '');
        return `<script type="text/wxtag-template"><style>html,body{margin:0;padding:0;-webkit-text-size-adjust:100%;text-size-adjust:100%}button{${css}}</style><button type="button">${escape(this.label)}</button>${'<' + '/script>'}`;
      } finally { button.disabled = disabled; }
    },
    current(completion) {
      return !this.destroyed && this.canCollect() && this.native && this.user && this.user.id === completion.user &&
        this.generation === completion.generation && this.grant === completion.grant && this.$refs.tag === completion.tag;
    },
    renderTag(completion) {
      this.detachTag();
      const tag = this.$refs.tag;
      if (!tag) return;
      completion.tag = tag;
      this.boundTag = tag;
      this.successHandler = e => this.finish(e, false, completion);
      this.errorHandler = e => {
        if (!this.current(completion)) return;
        if (this.ready) this.finish(e, true, completion);
        else this.unavailable('render-error', e.detail);
      };
      tag.addEventListener('success', this.successHandler);
      tag.addEventListener('error', this.errorHandler);
    },
    async finish(event, error, completion) {
      if (!this.current(completion) || !this.ready || !this.armed || this.busy) return;
      this.armed = false;
      if (error) this.failed = true;
      this.busy = true;
      let allow = false;
      try {
        const detail = event.detail || {};
        const statuses = typeof detail.subscribeDetails === 'string' ? JSON.parse(detail.subscribeDetails) : detail.subscribeDetails;
        const raw = statuses && statuses[this.template];
        const status = typeof raw === 'string' && raw.startsWith('{') ? JSON.parse(raw) : raw;
        const result = error ? 'error' : (status && status.status) || status;
        allow = result === 'accept';
        this.$emit('result', typeof result === 'string' ? result : 'unknown');
      } catch (e) { /* unknown native result does not create a grant */ }
      try { await completion.action(); }
      finally {
        // Record the accepted permission for its original account, independently of navigation
        // performed by the action. The server validates the key's identity/version scope.
        if (allow) accepted(completion.grant, completion.user);
        this.busy = false;
        if (this.generation === completion.generation) this.invalidate();
        if (!this.destroyed) { await this.$nextTick(); this.prepare(); }
      }
    },
    async plain() {
      if (this.busy || this.disabled || !this.fallback || this.destroyed) return;
      const action = this.action;
      // Cancel pending preparation as well as any mounted native control before the ordinary tap.
      this.invalidate();
      this.busy = true;
      try { await action(); }
      finally {
        this.busy = false;
        if (!this.destroyed) { await this.$nextTick(); this.prepare(); }
      }
    },
  },
};
</script>
<style scoped>
.wechat-action { position: relative; display: inline-block; height: 40px; vertical-align: middle; width: 100%; min-height: 40px; box-sizing: border-box; border: 1px solid #333; background: #eee; color: #222; }
.wechat-action.link { border: 0; background: none; color: #2674a0; text-decoration: underline; }
.wechat-action:disabled, .wechat-action > .native-fallback:disabled { color: #777; cursor: default; }
.wechat-action.link > .native-fallback { text-decoration: underline; }
.wechat-action > .native-tag, .wechat-action > .native-fallback { position: absolute; inset: 0; box-sizing: border-box; width: 100% !important; max-width: none !important; height: 100% !important; min-height: 0; margin: 0 !important; border: 0; border-radius: inherit; background: inherit; color: inherit; font: inherit; padding: 0; }
/* Match the ordinary action's content layout, including centered game placement. */
.wechat-action > .native-fallback { -webkit-appearance: none; appearance: none; display: inherit; align-items: inherit; justify-content: inherit; }
.wechat-action > .native-tag.pending { opacity: 0; pointer-events: none; }
</style>
